// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Wordloom — the DOM grid board. A square of cells; empty ones are wells, placed ones
// are tiles. Scoring cells are lit LIVE as words form (a column-scoring cell carries a
// vertical warp bar, a row-scoring cell a horizontal weft underline, a both-scoring
// cell both), so the mechanic teaches itself. One delegated pointer handler; taps read
// e.target's cell index (never elementFromPoint, which is null in a background tab).

import type { GridScore } from './score';

export interface BoardInput {
  cells: readonly (string | null)[];
  size: number;
  score: GridScore;
  /** Cell index just placed, for a settle animation. */
  lastPlaced: number | null;
  /** Cell index the player has staged to place next (desktop hover / focus preview). */
  preview: { cell: number; letter: string } | null;
  reducedMotion: boolean;
  /** In the final sweep we brighten every scoring word. */
  finalReveal: boolean;
}

export interface Board {
  render(input: BoardInput): void;
  destroy(): void;
}

export function createBoard(host: HTMLElement, onCellTap: (cellIdx: number) => void): Board {
  let size = 0;
  let cellEls: HTMLButtonElement[] = [];
  const grid = document.createElement('div');
  grid.className = 'grid';
  grid.setAttribute('role', 'grid');
  host.appendChild(grid);

  function onClick(e: Event): void {
    const t = (e.target as HTMLElement).closest<HTMLElement>('.cell');
    if (!t || !grid.contains(t)) return;
    const i = Number(t.dataset.i);
    if (Number.isFinite(i)) onCellTap(i);
  }
  grid.addEventListener('click', onClick);

  function build(n: number): void {
    size = n;
    grid.style.setProperty('--n', String(n));
    grid.innerHTML = '';
    cellEls = [];
    for (let i = 0; i < n * n; i++) {
      const cell = document.createElement('button');
      cell.className = 'cell';
      cell.type = 'button';
      cell.dataset.i = String(i);
      cell.setAttribute('role', 'gridcell');
      const glyph = document.createElement('span');
      glyph.className = 'glyph';
      cell.appendChild(glyph);
      grid.appendChild(cell);
      cellEls.push(cell);
    }
  }

  function render(input: BoardInput): void {
    if (size !== input.size) build(input.size);
    for (let i = 0; i < cellEls.length; i++) {
      const cell = cellEls[i];
      const ch = input.cells[i];
      const kinds = input.score.cellKinds.get(i);
      const preview = input.preview && input.preview.cell === i && ch == null ? input.preview.letter : '';

      const filled = ch != null;
      cell.classList.toggle('filled', filled);
      cell.classList.toggle('empty', !filled);
      cell.classList.toggle('preview', !!preview);
      cell.classList.toggle('sc-row', !!kinds?.has('row'));
      cell.classList.toggle('sc-col', !!kinds?.has('col'));
      cell.classList.toggle('sc-diag', !!kinds?.has('diag'));
      cell.classList.toggle('scoring', !!kinds);
      cell.classList.toggle('reveal', input.finalReveal && !!kinds);
      cell.setAttribute('aria-label', filled ? (ch as string) : preview ? `place ${preview}` : 'empty');
      cell.disabled = filled;

      const glyph = cell.firstElementChild as HTMLElement;
      glyph.textContent = filled ? (ch as string) : preview;

      if (i === input.lastPlaced && !input.reducedMotion) {
        cell.classList.remove('drop');
        void cell.offsetWidth;
        cell.classList.add('drop');
      }
    }
  }

  return {
    render,
    destroy(): void {
      grid.removeEventListener('click', onClick);
      grid.remove();
    },
  };
}
